import { checkboxClasses, styled, Theme } from '@mui/material';
import MuiCheckbox, { CheckboxProps as MuiCheckboxProps, CheckboxPropsColorOverrides } from '@mui/material/Checkbox';
import { OverridableStringUnion } from '@mui/types';
import { forwardRef } from 'react';
import colors from '../theme/colors';

declare module '@mui/material/Checkbox' {
  export interface CheckboxPropsColorOverrides {
    internal: true;
  }
}

interface CheckboxOwnerState {
  ownerState: {
    color: string;
    inverted?: boolean;
  };
}

export interface CheckboxProps extends MuiCheckboxProps {
  color?: OverridableStringUnion<
    'primary' | 'secondary' | 'success' | 'error' | 'warning' | 'info' | 'internal',
    CheckboxPropsColorOverrides
  >;
  inverted?: boolean;
}

const getColors = (defaultColor: string, disabled?: string, other?: string) => ({
  color: defaultColor,
  [`&.${checkboxClasses.disabled}`]: {
    color: disabled || defaultColor,
  },
  [`&.${checkboxClasses.checked}, &.${checkboxClasses.indeterminate}`]: {
    color: other || defaultColor,
  },
});

const getColorSetup = (theme: Theme, color: string, inverted?: boolean) =>
  ({
    secondary: getColors(!inverted ? theme.palette.secondary.main : colors.brandSecondary[100]),
    success: getColors(!inverted ? theme.palette.success.main : colors.success[100]),
    warning: getColors(!inverted ? theme.palette.warning.main : colors.warning[100]),
    info: getColors(!inverted ? theme.palette.info.main : colors.price[100]),
    error: getColors(!inverted ? theme.palette.error.main : colors.error[100]),
    internal: getColors(!inverted ? theme.palette.internal.main : colors.internal[100]),
    primary: !inverted
      ? getColors(colors.transparentGray[400], colors.transparentGray[400], theme.palette.primary.main)
      : getColors(colors.brandPrimary[100]),
  }[color === 'default' ? 'primary' : color]);

const StyledCheckbox = styled(MuiCheckbox, {
  name: 'O2Checkbox',
  slot: 'Checkbox',
  shouldForwardProp: (prop) => prop !== 'ownerState',
})<CheckboxOwnerState>(({ theme, ownerState }) => getColorSetup(theme, ownerState.color, ownerState.inverted));

const Checkbox = forwardRef<HTMLButtonElement, CheckboxProps>(({ inverted, color = 'primary', ...others }, ref) => {
  return <StyledCheckbox ref={ref} ownerState={{ inverted, color }} {...others} />;
});

Checkbox.displayName = 'Checkbox';

export default Checkbox;
